import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { UserField } from '../types';

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (taskData: any) => void;
  salesforceUsers: any[];
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ open, onClose, onAdd, salesforceUsers }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState<UserField | null>(null);
  const [requester, setRequester] = useState<UserField | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [type, setType] = useState('');
  const [priority, setPriority] = useState<'high' | 'normal' | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [types, setTypes] = useState<Array<{value: string, label: string}>>([]);

  // Load types from config
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const response = await fetch('/config/types.json');
        const data = await response.json();
        setTypes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load types:', error);
        setTypes([]);
      }
    };
    
    loadTypes();
  }, []);

  const handleClose = () => {
    // Reset form
    setTitle('');
    setDescription('');
    setAssignedTo(null);
    setRequester(null);
    setDueDate(null);
    setType('');
    setPriority('');
    setTags([]);
    onClose();
  };

  const handleAdd = () => {
    const taskData = {
      title,
      description,
      owner: assignedTo,
      requester,
      due: dueDate ? dueDate.toISOString() : null,
      type,
      priority: priority || null,
      tags,
    };
    onAdd(taskData);
    handleClose();
  };

  const isValid = title.trim() !== '' && description.trim() !== '';

  // Convert Salesforce user to UserField format
  const getUserFieldFromSalesforceUser = (user: any): UserField => ({
    userId: user.id || '',
    email: user.email || '',
    name: user.name || '',
    originalValue: user.name || '',
  });

  // Create unresolved UserField from free text
  const createUnresolvedUserField = (name: string): UserField => ({
    userId: '',
    email: '',
    name: '',
    originalValue: name,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title */}
            <TextField
              label="Title"
              required
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 200 }}
              helperText={`${title.length}/200 characters`}
            />

            {/* Description */}
            <TextField
              label="Description"
              required
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText={`${description.length}/2000 characters`}
            />

            {/* Assigned To */}
            <Autocomplete
              options={salesforceUsers}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || option.email || '';
              }}
              freeSolo
              value={assignedTo}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setAssignedTo(createUnresolvedUserField(newValue));
                } else if (newValue) {
                  setAssignedTo(getUserFieldFromSalesforceUser(newValue));
                } else {
                  setAssignedTo(null);
                }
              }}
              forcePopupIcon
              renderInput={(params) => (
                <TextField {...params} label="Assigned To" placeholder="Search or type name..." />
              )}
            />

            {/* Requester */}
            <Autocomplete
              options={salesforceUsers}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || option.email || '';
              }}
              freeSolo
              value={requester}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setRequester(createUnresolvedUserField(newValue));
                } else if (newValue) {
                  setRequester(getUserFieldFromSalesforceUser(newValue));
                } else {
                  setRequester(null);
                }
              }}
              forcePopupIcon
              renderInput={(params) => (
                <TextField {...params} label="Requester" placeholder="Search or type name..." />
              )}
            />

            {/* Due Date */}
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            {/* Type */}
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={type} onChange={(e) => setType(e.target.value)} label="Type">
                <MenuItem value="">None</MenuItem>
                {types.map((typeOption) => (
                  <MenuItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'high' | 'normal' | '')}
                label="Priority"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
              </Select>
            </FormControl>

            {/* Tags */}
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tags}
              onChange={(_, newValue) => setTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} {...getTagProps({ index })} key={index} />
                ))
              }
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Tags" 
                  placeholder="Type and press Enter to add tags"
                  helperText="Enter tags separated by commas or press Enter after each tag"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" color="success" disabled={!isValid}>
            Add Task
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddTaskModal;

